import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import type { createApiClient, PublicIdentity } from '@adieuu/shared';
import type { SpaceChannelMessagesState } from './types';

type ApiClient = ReturnType<typeof createApiClient>;

export interface SpaceParticipantProfilesParams {
  api: ApiClient;
  setParticipantProfiles: Dispatch<SetStateAction<Record<string, PublicIdentity>>>;
  /** Loaded channel messages; authors are auto-resolved as this changes. */
  messagesByChannel: Record<string, SpaceChannelMessagesState>;
}

/**
 * Author profile resolution for Space channel messages.
 *
 * Dedupes lookups via a stable ref set (a failed fetch is removed so it can be
 * retried) and auto-resolves senders whenever the loaded messages change.
 * Also caches Ed25519 signing public keys for Space message signature checks.
 *
 * Signing keys are deliberately fetched only for identities that have actually
 * posted, and only for the Space the message was posted in. Member lists call
 * {@link resolveProfiles}, which stays a profile-only lookup so browsing a Space
 * with many members does not pull a key per member.
 */
export function useSpaceParticipantProfiles(params: SpaceParticipantProfilesParams) {
  const { api, setParticipantProfiles, messagesByChannel } = params;

  const resolvedProfileIds = useRef<Set<string>>(new Set());
  const resolvedSigningKeyIds = useRef<Set<string>>(new Set());
  const [signingKeys, setSigningKeys] = useState<Record<string, string>>({});
  const [pqSigningKeys, setPqSigningKeys] = useState<Record<string, string>>({});

  const resolveProfiles = useCallback(
    async (ids: string[]) => {
      const missingProfiles = ids.filter((id) => !resolvedProfileIds.current.has(id));
      if (missingProfiles.length === 0) return;

      for (const id of missingProfiles) resolvedProfileIds.current.add(id);

      const fetched: Record<string, PublicIdentity> = {};
      await Promise.all(
        missingProfiles.map(async (id) => {
          try {
            const resp = await api.identity.getProfile(id);
            if (resp.data) {
              fetched[id] = resp.data;
            }
          } catch {
            resolvedProfileIds.current.delete(id);
          }
        }),
      );

      if (Object.keys(fetched).length > 0) {
        setParticipantProfiles((prev) => ({ ...prev, ...fetched }));
      }
    },
    [api, setParticipantProfiles],
  );

  const resolveSigningKeys = useCallback(
    async (authors: ReadonlyArray<{ identityId: string; spaceId: string }>) => {
      const missing = authors.filter((a) => !resolvedSigningKeyIds.current.has(a.identityId));
      if (missing.length === 0) return;

      for (const { identityId } of missing) resolvedSigningKeyIds.current.add(identityId);

      const fetchedKeys: Record<string, string> = {};
      const fetchedPqKeys: Record<string, string> = {};
      await Promise.all(
        missing.map(async ({ identityId, spaceId }) => {
          try {
            const keysResp = await api.identity.getPublicKeys(identityId, { spaceId });
            if (keysResp.data?.signingPublicKey) {
              fetchedKeys[identityId] = keysResp.data.signingPublicKey;
              fetchedPqKeys[identityId] = keysResp.data.pqSigningPublicKey ?? '';
            } else {
              // No key in the response (denied or not set up): allow a retry
              // rather than leaving the author permanently unverifiable.
              resolvedSigningKeyIds.current.delete(identityId);
            }
          } catch {
            resolvedSigningKeyIds.current.delete(identityId);
          }
        }),
      );

      if (Object.keys(fetchedKeys).length > 0) {
        setSigningKeys((prev) => ({ ...prev, ...fetchedKeys }));
        setPqSigningKeys((prev) => ({ ...prev, ...fetchedPqKeys }));
      }
    },
    [api],
  );

  // Auto-resolve senders whenever channel messages change. Each author is keyed
  // to the Space it posted in so the key lookup can be scoped to that Space.
  useEffect(() => {
    const authors = new Map<string, string>();
    for (const state of Object.values(messagesByChannel)) {
      for (const msg of state.messages) {
        if (!authors.has(msg.fromIdentityId)) {
          authors.set(msg.fromIdentityId, msg.spaceId);
        }
      }
    }
    if (authors.size === 0) return;
    void resolveProfiles([...authors.keys()]);
    void resolveSigningKeys(
      [...authors].map(([identityId, spaceId]) => ({ identityId, spaceId })),
    );
  }, [messagesByChannel, resolveProfiles, resolveSigningKeys]);

  const resolveProfilesPublic = useCallback(
    (ids: string[]) => {
      void resolveProfiles(ids);
    },
    [resolveProfiles],
  );

  const resolveSigningKeysPublic = useCallback(
    (spaceId: string, identityIds: string[]) => {
      void resolveSigningKeys(identityIds.map((identityId) => ({ identityId, spaceId })));
    },
    [resolveSigningKeys],
  );

  return {
    resolveProfiles,
    resolveProfilesPublic,
    resolveSigningKeys,
    resolveSigningKeysPublic,
    signingKeys,
    pqSigningKeys,
  };
}
